import React from 'react';
import { useLanguage } from '@/shared/hooks/useLanguage';
import { useTheme } from '@/app/contexts/ThemeContext';
import { OrderSummary, PaymentMethod } from '../types/pricing.types';

interface OrderSummaryCardProps {
  orderSummary: OrderSummary;
  onPayment: (method: PaymentMethod) => void;
  formatPrice: (price: number) => string;
}

const OrderSummaryCard: React.FC<OrderSummaryCardProps> = ({
  orderSummary,
  onPayment,
  formatPrice,
}) => {
  const { t, isRTL } = useLanguage();
  const { theme } = useTheme();
  const isDark = theme === 'dark';

  return (
    <div
      className={`${
        isDark ? 'bg-transparent' : 'bg-transparent'
      }`}
      dir={isRTL ? 'rtl' : 'ltr'}
    >
      <div className="space-y-4 mb-6">
        {/* Plan */}
        <div className="flex justify-between items-center">
          <span className={`${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
            {t('pricing.orderSummary.plan')}:
          </span>
          <span className={`font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>
            {orderSummary.plan.name}
          </span>
        </div>

        {/* Price */}
        <div className="flex justify-between items-center">
          <span className={`${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
            {t('pricing.orderSummary.price')}:
          </span>
          <span className={`font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>
            {formatPrice(orderSummary.subtotal)} {t('pricing.toman')}
          </span>
        </div>

        {/* Discount */}
        {orderSummary.discount > 0 && (
          <div className="flex justify-between items-center">
            <span className={`${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
              {t('pricing.orderSummary.discount')}:
            </span>
            <span className={`font-semibold text-green-500`}>
              -{formatPrice(orderSummary.discount)} {t('pricing.toman')}
            </span>
          </div>
        )}

        {/* Tax */}
        {orderSummary.tax > 0 && (
          <div className="flex justify-between items-center">
            <span className={`${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
              {t('pricing.orderSummary.tax')}:
            </span>
            <span className={`font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>
              {formatPrice(orderSummary.tax)} {t('pricing.toman')}
            </span>
          </div>
        )}

        {/* Divider */}
        <div className={`border-t ${isDark ? 'border-gray-700' : 'border-gray-200'} my-4`} />

        {/* Total */}
        <div className="flex justify-between items-center">
          <span className={`text-lg font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
            {t('pricing.orderSummary.total')}:
          </span>
          <span className={`text-2xl font-bold text-primary`}>
            {formatPrice(orderSummary.total)} {t('pricing.toman')}
          </span>
        </div>
      </div>

      {/* Payment Buttons */}
      {orderSummary.plan.id !== 'free' && (
        <div className="flex gap-4">
          <button
            onClick={() => onPayment('online')}
            className="flex-1 py-3 rounded-lg font-semibold bg-primary text-white hover:bg-primary/90 transition-colors"
          >
            {t('pricing.orderSummary.onlinePayment')}
          </button>
          <button
            onClick={() => onPayment('sheba')}
            className="flex-1 py-3 rounded-lg font-semibold border-2 border-primary text-primary hover:bg-primary/10 transition-colors"
          >
            {t('pricing.orderSummary.shebaPayment')}
          </button>
        </div>
      )}
    </div>
  );
};

export default OrderSummaryCard;

